"use client"

import React, { useState } from 'react'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogTrigger
} from "../../../shared/components/ui/dialog"
import { Button } from "../../../shared/components/ui/button"
import { Trash2 } from "lucide-react"
import { deleteIconPng } from "../../../shared/constants/images"
import Image from "next/image"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { HomeOwner } from "../types/backend-response.type"
import { deleteHomeowner } from "@/features/homeowners/api/homeowners.api"
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { useQueryClient } from '@tanstack/react-query'
import { HOME_OWNERS } from '@/shared/constants/backend-api'

interface DeleteHomeownerDialogProps {
    homeowner: HomeOwner
}

function DeleteHomeownerDialog({ homeowner }: DeleteHomeownerDialogProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const { session } = useSession()
    const queryClient = useQueryClient()

    // Only show delete button for admin users
    if (session?.role !== UserRole.ADMIN) {
        return null
    }

    const handleDeleteHomeowner = async () => {
        setIsLoading(true)
        try {
            const response = await deleteHomeowner(homeowner.id)
            if (!response.success) {
                errorToast(response.message || 'Failed to delete homeowner')
                return
            }
            successToast(`Homeowner ${homeowner.user.fullname} and all associated data deleted successfully`)
            setIsOpen(false)
            // Invalidate and refetch the homeowners list
            queryClient.invalidateQueries({ queryKey: [HOME_OWNERS.base] })
        } catch (error) {
            errorToast('An unexpected error occurred while deleting the homeowner')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon" 
                    className="w-7 h-7 p-1 bg-destructive text-white hover:bg-destructive/90 rounded-md"
                    title="Delete Homeowner"
                >
                    <Trash2 className="w-4 text-white" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[400px]">
                <Image
                    src={deleteIconPng}
                    alt="Delete Icon"
                    className='w-[80px] mx-auto'
                    priority
                />
                <DialogTitle className='w-5/6 text-center mx-auto font-medium leading-snug'>
                    Delete Homeowner and All Data?
                </DialogTitle>
                <div className="text-center text-sm text-muted-foreground space-y-2">
                    <p>This will permanently delete:</p>
                    <ul className="text-left list-disc list-inside space-y-1">
                        <li>Homeowner profile and contact information</li>
                        <li>Home details and property information</li>
                        <li>All work orders and progress data</li>
                        <li>Photos, documents, and attachments</li>
                        <li>All related records and history</li>
                    </ul>
                    <p className="font-medium text-destructive mt-3">
                        This action cannot be undone!
                    </p>
                </div>
                <DialogFooter className="gap-3 md:gap-2">
                    <DialogClose asChild>
                        <Button 
                            variant='outline' 
                            className='w-full h-11 rounded-lg' 
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        showLoader={isLoading}
                        disabled={isLoading}
                        className='w-full h-11 rounded-lg'
                        onClick={handleDeleteHomeowner}
                    >
                        Delete Permanently
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default DeleteHomeownerDialog
